import type { InboxGroup, InboxProvider, MailRule } from "../api/connectionHub.types";
import type { RecheckPhase } from "../components/accounts/RecheckPanel/RecheckPanel";
import { currentRule } from "../utils/outlookRules";

export type SetupStep = "forwarding" | "filter";

export interface PipCopyItem {
  label: string;
  value: string;
}

export interface SetupPipContent {
  title: string;
  video: string | null;
  rows: PipCopyItem[][];
}

interface SetupStepConfig {
  link: "forwardingSettings" | "filterSettings";
  pip: (group: InboxGroup) => SetupPipContent;
}

const GMAIL_FORWARDING_VIDEO = "https://meritoassets.s3.ap-south-1.amazonaws.com/emailOtpSteps/gmail_forwarding_address_set_up.mp4";
const GMAIL_FILTER_EMAIL_VIDEO = "https://meritoassets.s3.ap-south-1.amazonaws.com/emailOtpSteps/gmail_filter_only_emails.mp4";
const GMAIL_FILTER_WORDS_VIDEO = "https://meritoassets.s3.ap-south-1.amazonaws.com/emailOtpSteps/gmail_filter_emails_hasWords.mp4.mp4";
const OUTLOOK_RULE_EMAIL_VIDEO = "https://meritoassets.s3.ap-south-1.amazonaws.com/emailOtpSteps/outlook_filter_only_emails.mp4";
const OUTLOOK_RULE_WORDS_VIDEO = "https://meritoassets.s3.ap-south-1.amazonaws.com/emailOtpSteps/outlook_filter_emails_hasWords.mp4";

const FORWARDING_TITLE = "Setup Forwarding";
const FILTER_TITLE = "Setup Filters";

const present = (items: (PipCopyItem | null)[]): PipCopyItem[] => items.filter((item): item is PipCopyItem => Boolean(item?.value));

const outlookRuleRows = (rule: MailRule): PipCopyItem[][] => {
  const label = rule.field === "hasWords" ? "Subject or Body Includes" : "From";
  const conditions = rule.valuesAddedSeparately
    ? rule.conditionValues.map((value, index) => ({ label: `${label} ${index + 1}`, value }))
    : [{ label, value: rule.conditionValues.join(", ") }];
  const redirect = { label: "Redirect to", value: rule.redirectTo };
  const nameRow = present([{ label: "Rule Name", value: rule.name }]);
  return conditions.length > 1
    ? [nameRow, present(conditions), present([redirect])]
    : [nameRow, present([...conditions, redirect])];
};

export const SETUP_STEPS: Partial<Record<InboxProvider, Partial<Record<SetupStep, SetupStepConfig>>>> = {
  gmail: {
    forwarding: {
      link: "forwardingSettings",
      pip: (group) => ({
        title: FORWARDING_TITLE,
        video: GMAIL_FORWARDING_VIDEO,
        rows: [present([{ label: "Copy email to Forward to", value: group.forwardingAddress }])],
      }),
    },
    filter: {
      link: "filterSettings",
      pip: (group) => ({
        title: FILTER_TITLE,
        video: group.links.hasWords ? GMAIL_FILTER_WORDS_VIDEO : GMAIL_FILTER_EMAIL_VIDEO,
        rows: [present([
          group.links.filterFrom ? { label: "From", value: group.links.filterFrom } : null,
          group.links.filterHasWords ? { label: "Has Words", value: group.links.filterHasWords } : null,
        ])],
      }),
    },
  },
  outlook: {
    filter: {
      link: "forwardingSettings",
      pip: (group) => {
        const rule = currentRule(group.rules, group.covers);
        return {
          title: FILTER_TITLE,
          video: rule?.field === "hasWords" ? OUTLOOK_RULE_WORDS_VIDEO : OUTLOOK_RULE_EMAIL_VIDEO,
          rows: rule ? outlookRuleRows(rule) : [],
        };
      },
    },
  },
};

export const setupLinkOf = (group: InboxGroup, step: SetupStep): string | null => {
  const config = SETUP_STEPS[group.inbox.provider]?.[step];
  return config ? group.links[config.link] ?? null : null;
};

export const setupPipOf = (group: InboxGroup, step: SetupStep): SetupPipContent | null => {
  const content = SETUP_STEPS[group.inbox.provider]?.[step]?.pip(group);
  return content ? { ...content, rows: content.rows.filter((row) => row.length) } : null;
};

export const setupStepOf = (phase: RecheckPhase): SetupStep => (phase === "forwarding" ? "forwarding" : "filter");
